import { API_BASE_URL } from '../config/api';

export interface PricingPlan {
  id: string;
  brandId: string;
  brandName?: string;
  featureTypeId: string;
  featureCode: string;
  featureName: string;
  baseQuota: number;
  basePrice: number;
  overageUnit: number;
  overagePrice: number;
  currency: string;
  isActive: boolean;
  effectiveFrom: string;
  effectiveUntil?: string;
}

export interface Invoice {
  brandId: string;
  brandName: string;
  month: string;
  features: InvoiceFeature[];
  subtotal: number;
  total: number;
  currency: string;
}

export interface InvoiceFeature {
  featureCode: string;
  featureName: string;
  usageCount: number;
  baseQuota: number;
  overageCount: number;
  basePrice: number;
  overagePrice: number;
  totalPrice: number;
  currency: string;
}

export class PricingService {
  private static getAuthToken(): string | null {
    return localStorage.getItem('token');
  }

  private static getHeaders(): HeadersInit {
    const token = this.getAuthToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  /**
   * Get all pricing plans for a brand
   */
  public static async getPricingPlans(brandId: string): Promise<PricingPlan[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/pricing/brands/${brandId}`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data.plans;
    } catch (error) {
      console.error('Error fetching pricing plans:', error);
      throw error;
    }
  }

  /**
   * Get pricing plan for a specific feature
   */
  public static async getFeaturePricingPlan(
    brandId: string,
    featureCode: string
  ): Promise<PricingPlan> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/pricing/brands/${brandId}/features/${featureCode}`,
        {
          method: 'GET',
          headers: this.getHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error fetching feature pricing plan:', error);
      throw error;
    }
  }

  /**
   * Create or update pricing plan (Admin only)
   */
  public static async updatePricingPlan(
    brandId: string,
    featureCode: string,
    plan: Partial<PricingPlan>
  ): Promise<PricingPlan> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/pricing/brands/${brandId}/features/${featureCode}`,
        {
          method: 'POST',
          headers: this.getHeaders(),
          body: JSON.stringify(plan),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error updating pricing plan:', error);
      throw error;
    }
  }

  /**
   * Get monthly invoice
   */
  public static async getInvoice(brandId: string, month: string): Promise<Invoice> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/pricing/brands/${brandId}/invoices/${month}`,
        {
          method: 'GET',
          headers: this.getHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error fetching invoice:', error);
      throw error;
    }
  }

  /**
   * Get current month estimate
   */
  public static async getCurrentEstimate(brandId: string): Promise<Invoice> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/pricing/brands/${brandId}/invoices/current/estimate`,
        {
          method: 'GET',
          headers: this.getHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error fetching current estimate:', error);
      throw error;
    }
  }

  /**
   * Export invoice (PDF - would need backend support)
   */
  public static async exportInvoicePDF(brandId: string, month: string): Promise<Blob> {
    // For now, return a simple text file
    // In production, this would call a backend endpoint that generates PDF
    const invoice = await this.getInvoice(brandId, month);
    const content = this.formatInvoiceText(invoice);
    
    return new Blob([content], { type: 'text/plain' });
  }

  /**
   * Export invoice as CSV
   */
  public static async exportInvoiceCSV(brandId: string, month: string): Promise<Blob> {
    try {
      const invoice = await this.getInvoice(brandId, month);
      const csv = this.convertInvoiceToCSV(invoice);
      
      return new Blob([csv], { type: 'text/csv' });
    } catch (error) {
      console.error('Error exporting invoice CSV:', error);
      throw error;
    }
  }

  /**
   * Helper: Format invoice as text
   */
  private static formatInvoiceText(invoice: Invoice): string {
    const lines = [
      'FASHION CONNECT - INVOICE',
      '='.repeat(50),
      `Brand: ${invoice.brandName}`,
      `Month: ${invoice.month}`,
      `Currency: ${invoice.currency}`,
      '',
      'FEATURES:',
      '-'.repeat(50),
    ];

    invoice.features.forEach((feature) => {
      lines.push(
        `${feature.featureName}:`,
        `  Usage: ${feature.usageCount} (Base Quota: ${feature.baseQuota})`,
        `  Overage: ${feature.overageCount}`,
        `  Base Price: ${feature.basePrice} ${feature.currency}`,
        `  Overage Price: ${feature.overagePrice} ${feature.currency}`,
        `  Total: ${feature.totalPrice} ${feature.currency}`,
        ''
      );
    });

    lines.push(
      '-'.repeat(50),
      `TOTAL: ${invoice.total} ${invoice.currency}`,
      '='.repeat(50)
    );

    return lines.join('\n');
  }

  /**
   * Helper: Convert invoice to CSV
   */
  private static convertInvoiceToCSV(invoice: Invoice): string {
    const headers = [
      'Feature',
      'Usage Count',
      'Base Quota',
      'Overage',
      'Base Price',
      'Overage Price',
      'Total Price',
      'Currency',
    ];

    const rows = invoice.features.map((feature) => [
      feature.featureName,
      feature.usageCount,
      feature.baseQuota,
      feature.overageCount,
      feature.basePrice,
      feature.overagePrice,
      feature.totalPrice,
      feature.currency,
    ]);

    // Add total row
    rows.push([
      'TOTAL',
      '',
      '',
      '',
      '',
      '',
      invoice.total,
      invoice.currency,
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.join(',')),
    ].join('\n');

    return csvContent;
  }
}

